import type { Position } from '../../types';
import type { MovementState, PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { add, distance, normalize, scale, subtract, type Vector2 } from '../match/Vector2';

export interface AnchorProfile {
  preferredRadius: number;
  returnRadius: number;
}

export const ANCHOR_PROFILES: Record<Position, AnchorProfile> = {
  GK: { preferredRadius: 4, returnRadius: 1.5 },
  DF: { preferredRadius: 20, returnRadius: 7 },
  MF: { preferredRadius: 24, returnRadius: 8 },
  FW: { preferredRadius: 18, returnRadius: 7 },
};

const intentRadiusMultiplier = (state: MovementState): number => {
  if (state === 'MAKING_RUN' || state === 'TRACKING_RUN') return 1.35;
  if (state === 'PRESSING' || state === 'MARKING') return 1.35;
  return 1;
};

/** Applies a graded positional preference. It never acts as a legality clamp. */
export class AnchorPolicy {
  preferredRadius(position: Position): number {
    return ANCHOR_PROFILES[position].preferredRadius;
  }

  returnRadius(position: Position): number {
    return ANCHOR_PROFILES[position].returnRadius;
  }

  soften(player: PlayerRuntimeState, idealTarget: Vector2, softness: number, bypass = false): Vector2 {
    if (bypass) return { ...idealTarget };
    const offset = subtract(idealTarget, player.anchorPosition);
    const requestedDistance = distance(idealTarget, player.anchorPosition);
    const preferredRadius = player.preferredAnchorRadius * intentRadiusMultiplier(player.movementState);
    if (requestedDistance <= preferredRadius) return { ...idealTarget };
    const boundedSoftness = Math.max(0, Math.min(1, softness));
    const softenedDistance = preferredRadius + (requestedDistance - preferredRadius) * (1 - boundedSoftness);
    return add(player.anchorPosition, scale(normalize(offset), softenedDistance));
  }
}
